import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faBoxes,
  faStore,
  faTag,
  faDollarSign,
  faBox,
  faCalendarPlus,
  faSpinner,
  faSearch,
  faTimes,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { toastError, toastSuccess } from "../../../utils/toast";

const AddStock = ({ onAdd, onClose, shops = [], products = [] }) => {
  const [form, setForm] = useState({
    shopId: "",
    productId: "",
    dozen_amount: "",
    purchase_price: "",
    selling_price: "",
    purchase_date: new Date().toISOString().split("T")[0],
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Searchable product dropdown state
  const [productSearch, setProductSearch] = useState("");
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef(null);
  const productSearchInputRef = useRef(null);

  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);
  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products],
  );

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return safeProducts;
    const query = productSearch.toLowerCase().trim();
    return safeProducts.filter((product) => {
      const name = (product.product_name || "").toLowerCase();
      const category =
        product.product_category?.category_name?.toLowerCase() || "";
      const type =
        product.product_category?.product_type?.name?.toLowerCase() || "";
      return (
        name.includes(query) || category.includes(query) || type.includes(query)
      );
    });
  }, [safeProducts, productSearch]);

  const selectedProduct = useMemo(() => {
    if (!form.productId) return null;
    return safeProducts.find((p) => p.productId === form.productId) || null;
  }, [safeProducts, form.productId]);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        productDropdownRef.current &&
        !productDropdownRef.current.contains(event.target)
      ) {
        setIsProductDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isProductDropdownOpen && productSearchInputRef.current) {
      productSearchInputRef.current.focus();
    }
  }, [isProductDropdownOpen]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TZS";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectProduct = (product) => {
    setForm((prev) => ({ ...prev, productId: product.productId }));
    setIsProductDropdownOpen(false);
    setProductSearch("");
    if (formErrors.productId)
      setFormErrors((prev) => ({ ...prev, productId: "" }));
  };

  const handleClearProduct = (e) => {
    e.stopPropagation();
    setForm((prev) => ({ ...prev, productId: "" }));
    setProductSearch("");
    if (formErrors.productId)
      setFormErrors((prev) => ({ ...prev, productId: "" }));
  };

  const getTotalItems = () => {
    if (!selectedProduct || !form.dozen_amount) return 0;
    return (
      parseInt(form.dozen_amount || 0) *
      (Number(selectedProduct.no_of_items) || 1)
    );
  };

  const getTotalSelling = () => {
    const totalItems = getTotalItems();
    const single = parseFloat(form.selling_price);
    if (!totalItems || !single || single <= 0) return 0;
    return single * totalItems;
  };

  const getTotalPurchase = () => {
    const dozen = parseInt(form.dozen_amount || 0);
    const single = parseFloat(form.purchase_price);
    if (!dozen || !single || single <= 0) return 0;
    return single * dozen;
  };

  const getPackageTypeLabel = () => selectedProduct?.package_type || "Package";

  const handleSubmit = async () => {
    const errors = {};

    if (!form.shopId) errors.shopId = "Please select a shop";
    if (!form.productId) errors.productId = "Please select a product";
    if (!form.dozen_amount || parseInt(form.dozen_amount) < 1)
      errors.dozen_amount = "Please enter a valid amount";
    if (!form.purchase_price || parseFloat(form.purchase_price) < 0)
      errors.purchase_price = "Please enter a valid purchase price";
    if (!form.selling_price || parseFloat(form.selling_price) <= 0)
      errors.selling_price = "Please enter a valid selling price";
    if (!form.purchase_date)
      errors.purchase_date = "Please select a purchase date";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toastError("Please fix all validation errors before continuing.");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = onAdd({
        shopId: form.shopId,
        productId: form.productId,
        dozen_amount: parseInt(form.dozen_amount),
        purchase_price: parseFloat(form.purchase_price),
        selling_price: parseFloat(form.selling_price),
        purchase_date: form.purchase_date,
      });

      if (result && typeof result.then === "function") {
        try {
          await result;
          toastSuccess(
            `Stock for "${selectedProduct?.product_name || "product"}" added successfully!`,
          );
          onClose();
        } catch (error) {
          toastError(
            `Failed to add stock: ${error?.message || "Please try again."}`,
          );
        }
      } else {
        toastSuccess(
          `Stock for "${selectedProduct?.product_name || "product"}" added successfully!`,
        );
        onClose();
      }
    } catch (error) {
      toastError(
        `Failed to add stock: ${error?.message || "Please try again."}`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add new stock"
      maxWidth="sm:max-w-lg"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add stock
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <FontAwesomeIcon icon={faBoxes} className="text-blue-800 h-4 w-4" />
          </div>
          <div>
            <p className="text-sm text-gray-600 font-semibold">Add new stock</p>
            <p className="text-xs text-gray-600 font-medium">
              Fill in all required fields (*)
            </p>
          </div>
        </div>

        {/* Shop */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faStore}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Shop<span className="text-red-700">*</span>
          </Label>
          <Select
            value={form.shopId}
            onValueChange={(value) => {
              setForm((prev) => ({ ...prev, shopId: value }));
              if (formErrors.shopId)
                setFormErrors((prev) => ({ ...prev, shopId: "" }));
            }}
          >
            <SelectTrigger
              className={`h-10 text-sm ${
                formErrors.shopId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <SelectValue placeholder="Select shop" />
            </SelectTrigger>
            <SelectContent>
              {safeShops.map((shop) => (
                <SelectItem key={shop.shopId} value={shop.shopId}>
                  {shop.shop_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formErrors.shopId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.shopId}
            </p>
          )}
        </div>

        {/* Searchable Product Select */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faTag}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Product<span className="text-red-700">*</span>
          </Label>

          <div className="relative" ref={productDropdownRef}>
            {/* Trigger button */}
            <button
              type="button"
              onClick={() => setIsProductDropdownOpen((prev) => !prev)}
              className={`w-full h-10 text-sm rounded-md border bg-white px-3 flex items-center justify-between gap-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 ${
                formErrors.productId ? "border-red-500" : "border-gray-300"
              } hover:border-blue-400`}
            >
              <span
                className={`truncate ${
                  selectedProduct ? "text-gray-900" : "text-gray-400"
                }`}
              >
                {selectedProduct
                  ? selectedProduct.product_name
                  : "Select product"}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                {selectedProduct && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClearProduct}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleClearProduct(e);
                      }
                    }}
                    className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                    aria-label="Clear product"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                  </span>
                )}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-3 w-3 text-gray-400 transition-transform ${
                    isProductDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {/* Dropdown */}
            {isProductDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                {/* Search input */}
                <div className="p-2 border-b border-gray-100 bg-gray-50">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
                    />
                    <input
                      ref={productSearchInputRef}
                      type="text"
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search product by name, category..."
                      className="w-full h-9 pl-9 pr-8 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {productSearch && (
                      <button
                        type="button"
                        onClick={() => setProductSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        aria-label="Clear search"
                      >
                        <FontAwesomeIcon icon={faTimes} className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Product list */}
                <div className="max-h-60 overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-gray-500">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="h-5 w-5 mb-2 text-gray-300"
                      />
                      <p>No products found</p>
                      {productSearch && (
                        <p className="text-xs text-gray-400 mt-1">
                          Try a different search term
                        </p>
                      )}
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      const isSelected = form.productId === product.productId;
                      return (
                        <button
                          key={product.productId}
                          type="button"
                          onClick={() => handleSelectProduct(product)}
                          className={`w-full text-left px-3 py-2.5 text-sm transition-colors border-b border-gray-50 last:border-b-0 ${
                            isSelected
                              ? "bg-blue-50 text-blue-900 font-medium"
                              : "hover:bg-gray-50 text-gray-700"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium">
                                {product.product_name}
                              </p>
                              {(product.product_category?.category_name ||
                                product.product_category?.product_type
                                  ?.name) && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {[
                                    product.product_category?.product_type
                                      ?.name,
                                    product.product_category?.category_name,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </p>
                              )}
                            </div>
                            {isSelected && (
                              <span className="text-blue-600 text-xs shrink-0">
                                ✓
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer with count */}
                {filteredProducts.length > 0 && (
                  <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
                    {filteredProducts.length} product
                    {filteredProducts.length !== 1 ? "s" : ""}
                    {productSearch && ` matching "${productSearch}"`}
                  </div>
                )}
              </div>
            )}
          </div>

          {formErrors.productId && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.productId}
            </p>
          )}
        </div>

        {/* Selected Product Info */}
        {selectedProduct && (
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-gray-600">Product Type</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.product_category?.product_type?.name ||
                    "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Category</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.product_category?.category_name || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Package Type</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.package_type || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Items per package</p>
                <p className="font-medium text-gray-900">
                  {selectedProduct.no_of_items || 1}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dozen Amount */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faBox}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Amount <span className="text-red-700">*</span>
          </Label>
          <Input
            name="dozen_amount"
            type="number"
            min="1"
            value={form.dozen_amount}
            onChange={handleChange}
            placeholder="Enter number of packages"
            className={`h-10 text-sm ${
              formErrors.dozen_amount ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.dozen_amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.dozen_amount}
            </p>
          )}
          {selectedProduct && form.dozen_amount && (
            <p className="text-xs text-gray-600">
              Total Items: {getTotalItems()}
            </p>
          )}
        </div>

        {/* Purchase Price */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Purchase Price (per {getPackageTypeLabel()}){" "}
            <span className="text-red-700">*</span>
          </Label>
          <Input
            name="purchase_price"
            type="number"
            min="0"
            step="0.01"
            value={form.purchase_price}
            onChange={handleChange}
            placeholder={`Enter price per ${getPackageTypeLabel()}`}
            className={`h-10 text-sm ${
              formErrors.purchase_price ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.purchase_price && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.purchase_price}
            </p>
          )}
          {selectedProduct && form.purchase_price && (
            <p className="text-xs text-gray-600">
              Cost Per Item:{" "}
              {formatCurrency(
                parseFloat(form.purchase_price) /
                  (Number(selectedProduct.no_of_items) || 1),
              )}
            </p>
          )}
        </div>

        {/* Selling Price Per Item */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faDollarSign}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Selling Price (per product) <span className="text-red-700">*</span>
          </Label>
          <Input
            name="selling_price"
            type="number"
            min="0"
            step="0.01"
            value={form.selling_price}
            onChange={handleChange}
            placeholder="Enter selling price per item (e.g., 85000)"
            className={`h-10 text-sm ${
              formErrors.selling_price ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.selling_price && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.selling_price}
            </p>
          )}
        </div>

        {/* Purchase Date */}
        <div className="flex flex-col gap-1">
          <Label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-800 h-3.5 w-3.5"
            />
            Purchase Date <span className="text-red-700">*</span>
          </Label>
          <Input
            name="purchase_date"
            type="date"
            value={form.purchase_date}
            onChange={handleChange}
            className={`h-10 text-sm ${
              formErrors.purchase_date ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.purchase_date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {formErrors.purchase_date}
            </p>
          )}
        </div>

        {/* Summary Preview */}
        {form.purchase_price &&
          form.selling_price &&
          form.dozen_amount &&
          selectedProduct && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-gray-600">Total Items</p>
                  <p className="font-bold text-gray-900">{getTotalItems()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Total Selling Price</p>
                  <p className="font-bold text-green-600">
                    {formatCurrency(getTotalSelling())}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Total Purchase Price</p>
                  <p className="font-bold text-gray-900">
                    {formatCurrency(getTotalPurchase())}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Expected Profit</p>
                  <p
                    className={`font-bold ${
                      getTotalSelling() - getTotalPurchase() >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {formatCurrency(getTotalSelling() - getTotalPurchase())}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Cost Per Item</p>
                  <p className="font-bold text-gray-900">
                    {formatCurrency(
                      parseFloat(form.purchase_price || 0) /
                        (Number(selectedProduct.no_of_items) || 1),
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
      </div>
    </Modal>
  );
};

export default AddStock;
